import { createSlice } from '@reduxjs/toolkit';
import { organizationsApi } from '~/api/organizations-api';
export interface Resources {
  cloud_id: string;
  confluence_url: string;
  workspace_name: string;
}

export interface Confluence {
  id: string;
  created: string;
  resources: Resources;
}

export interface Channel {
  channel: {
    capabilities: {
      allowConversationStart: boolean;
      allowInlineImages: boolean;
      allowMultipleRecipients: boolean;
      allowOutgoingMessages: boolean;
      allowedFileAttachmentMimeTypes: string[];
      deliveryIdentifierTypes: string[];
      maxFileAttachmentCount: number;
      maxFileAttachmentSizeBytes: number;
      maxTotalFileAttachmentSizeBytes: number;
      outgoingAttachmentTypes: string[];
      richText: string[];
      threadingModel: string;
    };
    channelAccountConnectionRedirectUrl: string;
    channelDescription: string;
    channelLogoUrl: string;
    createdAt: string; // timestamp (ISO)
    id: string;
    name: string;
    webhookUrl: string;
    channel_account: null | Record<string, unknown>;
  };
}
export interface ConnectedInbox {
  value: string;
  label: string;
}
export interface Hubspot {
  connected_inboxes: ConnectedInbox[];
  channel: Channel;
  created: string; // e.g. "2025-08-29 14:15:05"
  updated: string; // e.g. "2025-08-29 15:34:28"
}
export interface MessengerPage {
  id: string;
  name: string;
  model: string | null;
}

export interface Messenger {
  created: string;
  updated: string;
  pages: MessengerPage[];
  messenger_page_ids: string[];
}

export interface InstagramAccount {
  user_id: string;
  instagram_id: string;
  instagram_username: string;
  model: string | null;
}

export interface Instagram {
  created: string;
  updated: string;
  accounts: InstagramAccount[];
  instagram_ids: string[];
}

export interface Dixa {
  triple_check_filters: boolean;
  connected: boolean;
  account_id: string;
  agent_id: string | null;
  model_id: string | null;
  mode: string;
  skip_sender_email_addresses: string[];
  allowed_sender_email_addresses: string[];
  custom_prompt: string;
  retention_days: number | null;
}

export interface Zendesk {
  triple_check_filters: boolean;
  connected: boolean;
  account_id: string;
  model_id: string | null;
  mode: string;
  public_reply_limit?: number;
  ticket_status: string | null;
  include_tag_context: boolean;
  include_custom_fields_context: boolean;
  skip_domain_addresses: string[];
  skip_recipient_email_addresses: string[];
  skip_sender_email_addresses: string[];
  allowed_sender_email_addresses: string[];
  custom_prompt: string;
  custom_prompt_internal_notes: string;
  webhook_id: string | null;
  trigger_id: number | null;
  auth_method: 'oauth' | 'api_token';
  admin_email: string | null;
  reply_email: string | null;
}

export interface Gorgias {
  triple_check_filters: boolean;
  connected: boolean;
  account_id: string;
  model_id: string | null;
  mode: string;
  public_reply_limit?: number;
  reply_email: string | null;
  skip_domain_addresses: string[];
  skip_sender_email_addresses: string[];
  allowed_sender_email_addresses: string[];
  custom_prompt: string;
  custom_prompt_internal_notes: string;
  retention_days: number | null;
}

export interface OutlookMailbox {
  triple_check_filters: boolean | null;
  email: string;
  draft_replies: boolean;
  skip_cc_only?: boolean;
  model_id: string | null;
  /** null = not configured on this mailbox — inherit from parent account */
  skip_sender_email_addresses: string[] | null;
  skip_sender_email_domains: string[] | null;
  custom_spam_categories: string[] | null;
  custom_prompt: string | null;
}

export interface MicrosoftAccount {
  triple_check_filters: boolean;
  home_account_id: string;
  email: string | null;
  owner_user_id: string | null;
  connected: boolean;
  draft_replies: boolean;
  skip_cc_only?: boolean;
  model_id: string | null;
  shared_mailboxes: OutlookMailbox[];
  tasks: Record<string, string> | null;
  /** Account-level filter fields — always present, never null */
  skip_sender_email_addresses: string[];
  skip_sender_email_domains: string[];
  custom_spam_categories: string[];
  custom_prompt: string;
  /** Set by the backend poll job when MSAL token acquisition fails. null = healthy.
   *  Known values: 'cache_lost' | 'invalid_grant' | 'interaction_required' |
   *  'consent_required' | 'consent_revoked' | other MSAL error string. */
  last_error?: string | null;
  last_error_at?: string | null;
}

export interface Microsoft {
  accounts: MicrosoftAccount[];
  retention_days?: number | null;
}

export interface GoogleAccount {
  triple_check_filters: boolean;
  email: string;
  owner_user_id: string | null;
  connected: boolean;
  draft_replies: boolean;
  skip_cc_only?: boolean;
  model_id: string | null;
  /** Account-level filter fields */
  skip_sender_email_addresses: string[];
  skip_sender_email_domains: string[];
  custom_spam_categories: string[];
  custom_prompt: string;
}

export interface Google {
  accounts: GoogleAccount[];
}

export interface SharePointAccount {
  home_account_id: string;
  email: string | null;
  owner_user_id: string | null;
  connected: boolean;
  crawl_personal_drive: boolean;
  site_url_filter: string[];
  account_email: string;
  last_crawled_at: string | null;
  delta_links: Record<string, string>;
}

export interface SharePoint {
  accounts: SharePointAccount[];
}

export interface OneDriveAccount {
  home_account_id: string;
  email: string | null;
  owner_user_id: string | null;
  connected: boolean;
  last_crawled_at: string | null;
  delta_link: string;
}

export interface OneDrive {
  accounts: OneDriveAccount[];
}

export interface Roccai {
  model_ids: string[];
}

export interface EasyTableLocation {
  token: string;
  name: string;
  enabled: boolean;
  allowed_booking_types: string[];
}

export interface EasyTable {
  connected: boolean;
  locations: EasyTableLocation[];
}

export interface SpectraHotel {
  hotel_code: string;
  installation_id: string;
  hotel_name: string;
  base_url: string;
  username: string;
  currency: string;
}

export interface Spectra {
  connected: boolean;
  hotels: SpectraHotel[];
}

export interface SipMapping {
  id?: string | null;
  phone_number: string;
  model_id: string | null;
  languages: string[];
  forwarding_uri: string | null;
  connected: boolean;
}

export interface BlockedPhoneNumber {
  phone_number: string;
  name: string;
}

export interface Sip {
  connected: boolean;
  mappings: SipMapping[];
  blocked_phone_numbers?: BlockedPhoneNumber[];
  token_configured?: boolean;
}

export interface Integrations {
  confluence?: Confluence;
  dixa?: Dixa;
  hubspot?: Hubspot;
  messenger?: Messenger;
  instagram?: Instagram;
  zendesk?: Zendesk;
  gorgias?: Gorgias;
  microsoft?: Microsoft;
  google?: Google;
  sharepoint?: SharePoint;
  onedrive?: OneDrive;
  roccai?: Roccai;
  easytable?: EasyTable;
  spectra?: Spectra;
  sip?: Sip;
  mailchimp?: { connected: boolean; list_id: string; list_name: string; double_opt_in: boolean };
}

interface IntegrationState {
  integrations: Integrations;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// 2) Provide actual values in the initializer
const initialState: IntegrationState = {
  integrations: {},
  status: 'idle',
  error: null,
};

const integrationSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Use RTK Query actions in the extraReducers
    builder
      .addMatcher(organizationsApi.endpoints.getOrganizationIntegrations.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(organizationsApi.endpoints.getOrganizationIntegrations.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.integrations = action.payload;
      })
      .addMatcher(organizationsApi.endpoints.getOrganizationIntegrations.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch integrations';
      });
  },
});

export default integrationSlice.reducer;
